import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { switchMap, take, catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { throwError } from 'rxjs';
import { AuthService } from '../../infrastructure/services/auth/auth.service';

const RETRY_HEADER = 'X-Auth-Retry';
const CORRELATION_HEADER = 'x-correlation-id';
const SESSION_HEADER = 'session_id';
const CHANNEL_HEADER = 'x-canal';
const DEFAULT_CHANNEL = 'WEB';
const SESSION_STORAGE_KEY = 'sanimal_session_id';
const TRACE_HEADER_ROUTES = [
  '/v1/clientes/sarlaft',
  '/v1/clientes/startsarlaftevaluation',
  '/v1/clientes/pullersarlaft',
  '/v1/clientes/membresiaservice',
  '/v1/clientes/membresiatokenservice',
];

function generateRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `req-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function normalizeSessionId(value: string): string {
  return value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 40);
}

function createSessionId(): string {
  const randomPart = Math.random().toString(36).slice(2, 14);
  const timePart = Date.now().toString(36);
  return normalizeSessionId(`${timePart}${randomPart}`);
}

function getOrCreateSessionId(): string {
  const fallback = createSessionId();

  if (typeof sessionStorage === 'undefined') {
    return fallback;
  }

  const current = sessionStorage.getItem(SESSION_STORAGE_KEY)?.trim() ?? '';
  const normalizedCurrent = normalizeSessionId(current);

  if (normalizedCurrent) {
    if (normalizedCurrent !== current) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, normalizedCurrent);
    }
    return normalizedCurrent;
  }

  const created = fallback;
  sessionStorage.setItem(SESSION_STORAGE_KEY, created);
  return created;
}

function requiresSarlaftHeaders(url: string): boolean {
  return TRACE_HEADER_ROUTES.some((route) => url.includes(route));
}

function buildRequestHeaders(req: Parameters<HttpInterceptorFn>[0], token: string): Record<string, string> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };

  if (!requiresSarlaftHeaders(req.url)) {
    return headers;
  }

  headers[CORRELATION_HEADER] = req.headers.get(CORRELATION_HEADER)?.trim() || generateRequestId();
  headers[SESSION_HEADER] = req.headers.get(SESSION_HEADER)?.trim() || getOrCreateSessionId();
  headers[CHANNEL_HEADER] = req.headers.get(CHANNEL_HEADER)?.trim() || DEFAULT_CHANNEL;

  return headers;
}

/**
 * Interceptor funcional de autenticación.
 *
 * Flujo por request:
 * 1. Si la URL es el endpoint de token → no interceptar (evita loop infinito)
 * 2. Si hay token válido (>10 min) → inyecta el token y envía
 * 3. Si el token expiró o no existe → refresca primero, luego envía
 * 4. En caso de 401 inesperado → fuerza refresco y reintenta una sola vez
 * 5. Sin token tras el refresco → redirige al inicio del cotizador
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Excluir el endpoint de obtención de token para evitar bucle infinito
  if (req.url.includes(environment.auth.tokenEndpoint)) {
    return next(req);
  }

  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.initializeAuth().pipe(
    take(1),
    switchMap(() => {
      const token = authService.getToken();

      // Sin token → redirigir al inicio sin bloquear la app
      if (!token) {
        router.navigate(['/cotizador']);
        return next(req);
      }

      const authReq = req.clone({
        setHeaders: buildRequestHeaders(req, token),
      });

      return next(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
          // 401 sin reintento previo: forzar refresco y reintentar una vez
          if (error.status === 401 && !req.headers.has(RETRY_HEADER)) {
            return authService.fetchToken().pipe(
              take(1),
              switchMap(() => {
                const retryReq = authReq.clone({
                  setHeaders: buildRequestHeaders(authReq, authService.getToken()),
                  headers: authReq.headers.set(RETRY_HEADER, 'true'),
                });
                return next(retryReq);
              }),
            );
          }
          return throwError(() => error);
        }),
      );
    }),
  );
};
